import type { Language } from './types/i18n.js'

/**
 * Estonian public-holiday display names.
 *
 * Holiday rows can arrive from different sources (the local seed, or the Humaans
 * import which stores English names like "Victory Day"/"Midsummer Day"). To keep
 * the Estonian UI consistent we normalise the displayed name from the holiday's
 * date to the official Estonian name.
 *
 * Fixed-date holidays are keyed by `MM-DD` (any year); the movable ones
 * (Good Friday, Easter Sunday, Whitsun) are derived from the computed Easter
 * date, so they resolve for any year without table maintenance.
 */

// Fixed-date national holidays — same calendar day every year. Estonian
// holiday names are common nouns (nimetused), so they start lowercase; only
// proper nouns within them ("Eesti") keep a capital.
const FIXED_BY_MONTH_DAY: Record<string, string> = {
    '01-01': 'uusaasta',
    '02-24': 'Eesti Vabariigi aastapäev',
    '05-01': 'kevadpüha',
    '06-23': 'võidupüha',
    '06-24': 'jaanipäev',
    '08-20': 'taasiseseisvumispäev',
    '12-24': 'jõululaupäev',
    '12-25': 'esimene jõulupüha',
    '12-26': 'teine jõulupüha',
}

// Easter Sunday for a Gregorian-calendar year (anonymous Gregorian computus),
// as a UTC timestamp. Drives the three movable Estonian holidays.
const easterSundayUtc = (year: number): number => {
    const a = year % 19
    const b = Math.floor(year / 100)
    const c = year % 100
    const d = Math.floor(b / 4)
    const e = b % 4
    const f = Math.floor((b + 8) / 25)
    const g = Math.floor((b - f + 1) / 3)
    const h = (19 * a + b - d - g + 15) % 30
    const i = Math.floor(c / 4)
    const k = c % 4
    const l = (32 + 2 * e + 2 * i - h - k) % 7
    const m = Math.floor((a + 11 * h + 22 * l) / 451)
    const month = Math.floor((h + l - 7 * m + 114) / 31) // 3 = March, 4 = April
    const day = ((h + l - 7 * m + 114) % 31) + 1
    return Date.UTC(year, month - 1, day)
}

const DAY_MS = 24 * 60 * 60 * 1000

// Estonian movable holidays as day offsets from Easter Sunday.
const MOVABLE_BY_EASTER_OFFSET: Record<number, string> = {
    [-2]: 'suur reede',
    [0]: 'ülestõusmispühade 1. püha',
    [49]: 'nelipühade 1. püha',
}

const movableHolidayName = (isoDate: string): string | undefined => {
    const year = Number(isoDate.slice(0, 4))
    if (!Number.isFinite(year)) return undefined
    const dateUtc = Date.UTC(year, Number(isoDate.slice(5, 7)) - 1, Number(isoDate.slice(8, 10)))
    const offset = Math.round((dateUtc - easterSundayUtc(year)) / DAY_MS)
    return MOVABLE_BY_EASTER_OFFSET[offset]
}

/**
 * Returns the official Estonian name for the holiday on `isoDate` (YYYY-MM-DD)
 * when the UI language is Estonian, falling back to the stored name. For other
 * languages the stored (source) name is kept.
 *
 * A stored name that already is the Estonian name (differing at most in letter
 * case) is kept as-is, so a casing chosen in the admin sticks instead of being
 * normalised away.
 */
export const localizeHolidayName = (isoDate: string, fallback: string, lang: Language): string => {
    if (lang !== 'et') return fallback
    const canonical = movableHolidayName(isoDate) ?? FIXED_BY_MONTH_DAY[isoDate.slice(5, 10)]
    if (canonical === undefined) return fallback
    return fallback.toLowerCase() === canonical.toLowerCase() ? fallback : canonical
}
